import jsPDF from 'jspdf';

export const generatePDF = (gameplan, clientInfo, responses) => {
  // Create a new jsPDF instance
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 12, isBold = false, color = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(...color);
    
    const lines = doc.splitTextToSize(text, pageWidth - 40);
    checkPageBreak(lines.length * fontSize * 0.35 + 10);
    
    lines.forEach(line => {
      doc.text(line, 20, yPosition);
      yPosition += fontSize * 0.35;
    });
    yPosition += 5;
  };

  // Helper function to add colored section
  const addSection = (title, bgColor = [59, 130, 246]) => {
    checkPageBreak(30);
    
    // Add colored background
    doc.setFillColor(...bgColor);
    doc.rect(20, yPosition - 5, pageWidth - 40, 15, 'F');
    
    // Add title in white
    addText(title, 14, true, [255, 255, 255]);
    yPosition += 5;
  };

  // Helper function to add a card
  const addCard = (title, content, borderColor = [200, 200, 200]) => {
    checkPageBreak(40);
    
    // Card border
    doc.setDrawColor(...borderColor);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, yPosition - 5, pageWidth - 40, 35, 3, 3, 'FD');
    
    // Card title
    yPosition += 5;
    addText(title, 11, true, [31, 41, 55]);
    
    // Card content
    addText(content, 10, false, [75, 85, 99]);
    yPosition += 5;
  };

  // Helper function to add answer card
  const addAnswerCard = (question, answer) => {
    checkPageBreak(50);
    
    // Card border
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, yPosition - 5, pageWidth - 40, 45, 3, 3, 'FD');
    
    // Question title
    yPosition += 5;
    addText(question, 10, true, [31, 41, 55]);
    
    // Answer content
    const answerText = Array.isArray(answer) ? answer.join(', ') : answer;
    addText(answerText, 9, false, [55, 65, 81]);
    yPosition += 5;
  };

  // Cover page
  // Header with gradient effect simulation
  doc.setFillColor(59, 130, 246);
  doc.rect(0, 0, pageWidth, 60, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('2026 Business Gameplan', pageWidth / 2, 35, { align: 'center' });
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Your Personalized 12-Month Action Plan', pageWidth / 2, 45, { align: 'center' });
  
  // Client info on cover
  doc.setFontSize(12);
  if (clientInfo.name) doc.text(`Prepared for: ${clientInfo.name}`, pageWidth / 2, 55, { align: 'center' });
  if (clientInfo.email) doc.text(`${clientInfo.email}`, pageWidth / 2, 60, { align: 'center' });
  
  yPosition = 80;
  doc.setTextColor(0, 0, 0);
  
  // Generated date
  addText(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 10, false, [107, 114, 128]);
  yPosition += 20;

  // Table of Contents
  addSection('Table of Contents', [59, 130, 246]);
  const sections = [
    'Client Information',
    'Your Questionnaire Answers',
    'The Rule of 5 Ones',
    '2025 Insights and Analysis',
    'Revenue Targets',
    'Content Strategy',
    'Daily Action Plan',
    'Weekly Action Plan',
    'Monthly Action Plan',
    'Performance Metrics',
    'Commitment Statement'
  ];
  
  sections.forEach((section, index) => {
    addText(`${index + 1}. ${section}`, 10, false, [55, 65, 81]);
  });
  yPosition += 10;

  // Client Information Section
  addSection('Client Information', [59, 130, 246]);
  
  addAnswerCard('Name', clientInfo.name || 'Not provided');
  addAnswerCard('Email', clientInfo.email || 'Not provided');
  addAnswerCard('Business Name', responses.business_name || 'Not provided');
  addAnswerCard('Business Stage', responses.business_stage || 'Not provided');
  yPosition += 10;

  // Detailed Questionnaire Answers Section
  addSection('Your Complete Questionnaire Answers', [168, 85, 247]);
  
  // Personal Info Section
  addText('Personal Information', 12, true, [31, 41, 55]);
  addAnswerCard('Name', responses.name || 'Not provided');
  addAnswerCard('Business Name', responses.business_name || 'Not provided');
  addAnswerCard('Business Stage', responses.business_stage || 'Not provided');
  yPosition += 10;

  // Rule of 5 Ones Section
  addText('The Rule of 5 Ones', 12, true, [31, 41, 55]);
  addAnswerCard('Target Market', responses.target_market || 'Not provided');
  addAnswerCard('Main Offer', responses.offer || 'Not provided');
  addAnswerCard('Conversion Mechanism', responses.conversion_mechanism || 'Not provided');
  addAnswerCard('Problem Solved', responses.problem_solved || 'Not provided');
  addAnswerCard('Launch Date', responses.launch_date || 'Not provided');
  addAnswerCard('Price Point', responses.price_point ? `$${responses.price_point}` : 'Not provided');
  yPosition += 10;

  // 2025 Audit Section
  addText('2025 Business Audit', 12, true, [31, 41, 55]);
  addAnswerCard('Best Month Revenue', responses.best_month_revenue || 'Not provided');
  addAnswerCard('Success Factors', responses.success_factors || 'Not provided');
  addAnswerCard('Audience Growth Strategy', responses.audience_growth || 'Not provided');
  addAnswerCard('Top Content Types', responses.top_content_types ? responses.top_content_types.join(', ') : 'Not provided');
  addAnswerCard('Consistency Challenges', responses.consistency_challenges || 'Not provided');
  yPosition += 10;

  // Revenue Planning Section
  addText('Revenue Planning', 12, true, [31, 41, 55]);
  addAnswerCard('Monthly Income Goal', responses.monthly_income_goal ? `$${responses.monthly_income_goal}` : 'Not provided');
  addAnswerCard('Expected Conversion Rate', responses.conversion_rate ? `${responses.conversion_rate}%` : 'Not provided');
  yPosition += 10;

  // Content Audit Section
  addText('Content Performance Analysis', 12, true, [31, 41, 55]);
  addAnswerCard('Top Performing Post #1', responses.top_post_1 || 'Not provided');
  addAnswerCard('Why It Performed Well #1', responses.top_post_1_reason || 'Not provided');
  addAnswerCard('Top Performing Post #2', responses.top_post_2 || 'Not provided');
  addAnswerCard('Why It Performed Well #2', responses.top_post_2_reason || 'Not provided');
  addAnswerCard('Content Themes', responses.content_themes ? responses.content_themes.join(', ') : 'Not provided');
  yPosition += 10;

  // Posting Schedule Section
  addText('Posting Schedule', 12, true, [31, 41, 55]);
  addAnswerCard('Posting Level', responses.posting_level || 'Not provided');
  addAnswerCard('Content Batching Day', responses.content_batch_day || 'Not provided');
  yPosition += 10;

  // Daily Actions Section
  addText('Daily Action Plan', 12, true, [31, 41, 55]);
  addAnswerCard('Daily Priority Tasks', responses.daily_priority_tasks ? responses.daily_priority_tasks.join(', ') : 'Not provided');
  addAnswerCard('Daily Extra Tasks', responses.daily_extra_tasks ? responses.daily_extra_tasks.join(', ') : 'Not provided');
  yPosition += 10;

  // Weekly Actions Section
  addText('Weekly Action Plan', 12, true, [31, 41, 55]);
  addAnswerCard('Weekly Priority Tasks', responses.weekly_priority_tasks ? responses.weekly_priority_tasks.join(', ') : 'Not provided');
  addAnswerCard('Weekly Extra Tasks', responses.weekly_extra_tasks ? responses.weekly_extra_tasks.join(', ') : 'Not provided');
  yPosition += 10;

  // Monthly Actions Section
  addText('Monthly Action Plan', 12, true, [31, 41, 55]);
  addAnswerCard('Monthly Priority Tasks', responses.monthly_priority_tasks ? responses.monthly_priority_tasks.join(', ') : 'Not provided');
  addAnswerCard('Monthly Extra Tasks', responses.monthly_extra_tasks ? responses.monthly_extra_tasks.join(', ') : 'Not provided');
  yPosition += 10;

  // Tracking Metrics Section
  addText('Tracking & Analytics', 12, true, [31, 41, 55]);
  addAnswerCard('Primary Metrics', responses.primary_metrics ? responses.primary_metrics.join(', ') : 'Not provided');
  addAnswerCard('Tracking Method', responses.tracking_method || 'Not provided');
  yPosition += 10;

  // Commitment Section
  addText('Commitment Statement', 12, true, [31, 41, 55]);
  addAnswerCard('Word for 2026', responses.word_for_2026 || 'Not provided');
  addAnswerCard('How Will Show Up', responses.how_show_up || 'Not provided');
  addAnswerCard('Non-Negotiable', responses.non_negotiable || 'Not provided');
  addAnswerCard('End of Year Vision', responses.end_of_year_vision || 'Not provided');
  addAnswerCard('Why for 2026', responses.why_2026 || 'Not provided');
  yPosition += 10;

  // Confidence Check Section
  addText('Confidence & Readiness', 12, true, [31, 41, 55]);
  addAnswerCard('Confidence Level', responses.confidence_level ? `${responses.confidence_level}/10` : 'Not provided');
  addAnswerCard('Biggest Challenge', responses.biggest_challenge || 'Not provided');
  addAnswerCard('Support Needed', responses.support_needed ? responses.support_needed.join(', ') : 'Not provided');
  addAnswerCard('Digital Signature', responses.commitment_signature || 'Not provided');
  yPosition += 10;

  // Generated Gameplan Summary
  addSection('Generated Gameplan Summary', [59, 130, 246]);
  
  // Rule of 5 Ones Summary
  addText('The Rule of 5 Ones', 12, true, [31, 41, 55]);
  const fiveOnesCards = [
    { title: 'Target Market', content: gameplan.fiveOnes.targetMarket },
    { title: 'Offer', content: gameplan.fiveOnes.offer },
    { title: 'Conversion Mechanism', content: gameplan.fiveOnes.conversionMechanism },
    { title: 'Problem Solved', content: gameplan.fiveOnes.problemSolved },
    { title: 'Launch Date', content: gameplan.fiveOnes.launchDate },
    { title: 'Price Point', content: `$${gameplan.fiveOnes.pricePoint}` }
  ];

  fiveOnesCards.forEach((card, index) => {
    const colors = [
      [59, 130, 246], [34, 197, 94], [168, 85, 247],
      [251, 146, 60], [239, 68, 68], [99, 102, 241]
    ];
    addCard(card.title, card.content, colors[index]);
  });

  // Revenue Goals Summary
  addText('Revenue Targets', 12, true, [31, 41, 55]);
  const revenueCards = [
    { title: 'Monthly Goal', content: `$${gameplan.revenue.monthlyGoal?.toLocaleString()}` },
    { title: 'Daily Traffic Goal', content: gameplan.revenue.dailyTrafficGoal },
    { title: 'Conversion Rate', content: `${gameplan.revenue.conversionRate}%` }
  ];

  revenueCards.forEach(card => {
    addCard(card.title, card.content, [34, 197, 94]);
  });

  // Calculation breakdown
  addCard('Calculation Breakdown', 
    `Monthly Sales Goal: ${gameplan.revenue.monthlySalesGoal} sales\n` +
    `Monthly Traffic Goal: ${gameplan.revenue.monthlyTrafficGoal} visitors\n` +
    `Average Order Value: $${gameplan.revenue.averageOrderValue}`,
    [200, 200, 200]
  );

  // Action Plans Summary
  addText('Action Plans', 12, true, [31, 41, 55]);
  
  // Daily Actions
  addText('Daily Priority Tasks', 11, true, [31, 41, 55]);
  gameplan.actions.daily.forEach((task, index) => {
    addText(`${index + 1}. ${task}`, 10, false, [55, 65, 81]);
  });
  
  addText('Weekly Priority Tasks', 11, true, [31, 41, 55]);
  gameplan.actions.weekly.forEach((task, index) => {
    addText(`${index + 1}. ${task}`, 10, false, [55, 65, 81]);
  });

  addText('Monthly Priority Tasks', 11, true, [31, 41, 55]);
  gameplan.actions.monthly.forEach((task, index) => {
    addText(`${index + 1}. ${task}`, 10, false, [55, 65, 81]);
  });

  // Commitment Summary
  addText('Commitment Statement', 12, true, [31, 41, 55]);
  const commitmentItems = [
    { title: 'My Word for 2026', content: gameplan.commitment.word },
    { title: 'How I Will Show Up', content: gameplan.commitment.showUp },
    { title: 'My Non-Negotiable', content: gameplan.commitment.nonNegotiable },
    { title: 'End of Year Vision', content: gameplan.commitment.endOfYear },
    { title: 'My Why', content: gameplan.commitment.why }
  ];

  commitmentItems.forEach(item => {
    addCard(item.title, item.content, [251, 146, 60]);
  });

  // Footer
  checkPageBreak(30);
  doc.setFillColor(243, 244, 246);
  doc.rect(0, pageHeight - 30, pageWidth, 30, 'F');
  
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(10);
  doc.text('Generated by Optimal AI + Marketing', pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text('© 2026 Business Gameplan. All rights reserved.', pageWidth / 2, pageHeight - 10, { align: 'center' });

  return doc;
};